// src/views/TradingRobots/LinkAccount.jsx
import { useState, useEffect, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
// Frameworks
import {
  Box,
  Typography,
  Modal,
  Fade,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import MenuItem from "@mui/material/MenuItem";
import useAppDispatch from "hooks/useAppDispatch";
import { useTranslation } from "react-i18next";
// Custom imports
import { show } from "store/reducers/notifications.reducer";
// Assets
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import DnsIcon from "@mui/icons-material/Dns";
import NumbersIcon from "@mui/icons-material/Numbers";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import customAxios from "lib/axios";

const LinkAccount = function () {
  const history = useHistory();
  const localUserId = localStorage.getItem("user_id");
  const dispatch = useAppDispatch();
  const isUserLoggedIn =
    localUserId && !Number.isNaN(localStorage.getItem("user_id"));
  const [t] = useTranslation(["tradingRobots", "errors"]);

  const [showPassword, setShowPassword] = useState(false);

  // Valores visibles en los inputs
  const [platform, setPlatform] = useState("");
  const [broker, setBroker] = useState("");
  const [server, setServer] = useState("");
  const [login_no_autofill, setLogin] = useState("");
  const [password, setPassword] = useState("");

  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  // Datos para selects
  const [brokerNames, setBrokerNames] = useState([
    { BROKER_ID: "", NAME: "" },
  ]);
  const [serverNames, setServerNames] = useState([
    { SERVER_ID: "", NAME: "" },
  ]);

  // IDs que viajan al backend
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [selectedBrokerId, setSelectedBrokerId] = useState("");
  const [selectedServerId, setSelectedServerId] = useState("");

  // Refs para guiar el foco
  const brokerRef = useRef(null);
  const serverRef = useRef(null);
  const loginRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    const apiCalls = async () => {
      try {
        const respond = await customAxios(`/tradingrobots/getBrokers`);
        const rows = Array.isArray(respond.data) ? respond.data : [];

        // Rama Willybit: solo mostrar brokers asignados al usuario.
        const filtered = rows.filter(
          (row) => String(row?.BROKER_X_USER || "").trim().toUpperCase() === "Y"
        );
        setBrokerNames(filtered.length ? filtered : [{ BROKER_ID: "", NAME: "" }]);
      } catch (error) {
        dispatch(show({ type: "error", msg: t("errors:serverError") }));
      }
    };

    apiCalls();
  }, [dispatch, t]);

  useEffect(() => {}, [isUserLoggedIn]);

  const normalizeIsMt4 = (value) => {
    if (typeof value === "string") {
      const v = value.trim().toUpperCase();
      if (v === "Y" || v === "YES" || v === "1" || v === "MT4") return true;
      if (v === "N" || v === "NO" || v === "0" || v === "MT5") return false;
    }
    if (typeof value === "number") return value !== 0;
    if (typeof value === "boolean") return value;
    return true;
  };

  const getServerList = (brokerid, platformValue) => {
    const apiCalls = async () => {
      try {
        const respond = await customAxios(
          `/tradingrobots/getServers/${brokerid}`
        );
        const raw = Array.isArray(respond.data) ? respond.data : [];
        const wantMt4 = platformValue === "MT4";
        const filtered = raw.filter((srv) => {
          const isMt4 = normalizeIsMt4(srv.MT4_SERVER);
          return wantMt4 ? isMt4 : !isMt4;
        });
        setServerNames(
          filtered.length ? filtered : [{ SERVER_ID: "", NAME: "" }]
        );
      } catch (error) {
        dispatch(show({ type: "error", msg: t("errors:serverError") }));
      }
    };
    if (brokerid !== "") {
      apiCalls();
    } else {
      setServerNames([{ SERVER_ID: "", NAME: "" }]);
    }
  };

  // Estados de desactivación por pasos
  const isBrokerDisabled = !platform;
  const isServerDisabled = !broker;
  const isLoginDisabled = !server;
  const isPasswordDisabled = !login_no_autofill;

  // Validación básica de campos antes de enviar
  const isFormValid =
    selectedPlatform &&
    selectedBrokerId &&
    selectedServerId &&
    String(login_no_autofill).trim().length > 0 &&
    String(password).trim().length > 0;

  const onSubmit = () => {
    if (!isFormValid || loading) {
      dispatch(
        show({
          type: "error",
          msg:
            t("tradingRobots:LinkAccount.fillAllFields", {
              defaultValue: "Por favor, completa todos los campos.",
            }) || "Por favor, completa todos los campos.",
        })
      );
      return;
    }

    setLoading(true); // Desactiva boton mientras se vincula la cuenta
    const userId = localStorage.getItem("user_id");
    customAxios
      .post("/tradingrobots/linkAccount/", {
        userid: userId,
        brokerid: selectedBrokerId,
        serverid: selectedServerId,
        account: login_no_autofill,
        password: password,
      })
      .then((res) => {
        if (res.data.success) {
          history.push("/tradingrobots/LinkingProgress");
        } else {
          setLoading(false);
          if (res.data.message === "Account already linked") {
            dispatch(
              show({ type: "error", msg: t("errors:accountAlreadyLinked") })
            );
          } else {
            dispatch(show({ type: "error", msg: res.data.message }));
          }
        }
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
        if (
          e.response &&
          e.response.data &&
          e.response.data.message === "Account already linked"
        ) {
          dispatch(
            show({ type: "error", msg: t("errors:accountAlreadyLinked") })
          );
        } else {
          dispatch(
            show({
              type: "error",
              msg: t("errors:unexpectedError", {
                defaultValue: "Error inesperado, contacte con soporte",
              }),
            })
          );
        }
      });
  };

  // 🔹 Para que Enter dispare el envío igual que el botón
  const handleFormSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  // Listas para selects
  const platformList = useMemo(
    () => [
      { value: "MT4", label: "MetaTrader 4" },
      { value: "MT5", label: "MetaTrader 5" },
    ],
    []
  );

  const brokerList = useMemo(
    () =>
      brokerNames.map((curName, i) => (
        <MenuItem key={i} value={curName.BROKER_ID}>
          {curName.NAME}
        </MenuItem>
      )),
    [brokerNames]
  );

  const serverList = useMemo(
    () =>
      serverNames.map((curName, i) => (
        <MenuItem key={i} value={curName.SERVER_ID}>
          {curName.NAME}
        </MenuItem>
      )),
    [serverNames]
  );

  // Handlers con guía de foco y lógica por pasos
  const handlePlatformChange = (e) => {
    const value = e.target.value;
    setPlatform(value);
    setSelectedPlatform(value);
    setBroker("");
    setSelectedBrokerId("");
    setServer("");
    setSelectedServerId("");
    setLogin("");
    setPassword("");
    setServerNames([{ SERVER_ID: "", NAME: "" }]);

    setTimeout(() => {
      if (brokerRef.current) {
        brokerRef.current.focus();
      }
    }, 0);
  };

  const handleBrokerChange = (e) => {
    const value = e.target.value;
    setBroker(value);
    setSelectedBrokerId(value);
    setServer("");
    setSelectedServerId("");
    setLogin("");
    setPassword("");
    getServerList(value, platform);

    // Al seleccionar broker, siguiente paso: servidor
    setTimeout(() => {
      if (serverRef.current) {
        serverRef.current.focus();
      }
    }, 0);
  };

  const handleServerChange = (e) => {
    const value = e.target.value;
    setServer(value);
    setSelectedServerId(value);
    setLogin("");
    setPassword("");

    // Al seleccionar servidor, siguiente paso: login
    setTimeout(() => {
      if (loginRef.current) {
        loginRef.current.focus();
      }
    }, 0);
  };

  const handleLoginChange = (e) => {
    // ✅ Solo dígitos, pero sin cambiar el foco automáticamente
    const value = e.target.value.replace(/\D/g, "");
    setLogin(value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  return (
    <Modal
      open={open}
      disableAutoFocus
      disableRestoreFocus
      closeAfterTransition
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(6px)",
      }}
    >
      <Fade in={open}>
        {/* ✅ Lo convertimos en <form> para capturar Enter */}
        <Box
          component="form"
          onSubmit={handleFormSubmit}
          bgcolor="#1F2557"
          px={4}
          py={1}
          width={320}
          minHeight={480}
          borderRadius="10px"
          display="flex"
          flexDirection="column"
          color="white"
          textAlign="center"
          lineHeight="1.2"
          position="relative"
          sx={{
            "& .MuiTypography-root": {
              color: "#FFFFFF !important",
            },
            "& .MuiTypography-colorSecondary": {
              color: "rgba(255,255,255,0.85) !important",
            },
            "& .MuiInputBase-root": {
              color: "#FFFFFF !important",
            },
            "& .MuiInputLabel-root": {
              color: "rgba(255,255,255,0.85) !important",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#FFFFFF !important",
            },
            "& .MuiOutlinedInput-input": {
              color: "#FFFFFF !important",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.45) !important",
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.7) !important",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderColor: "#FF8B00 !important",
              },
            "& .MuiInputAdornment-root .MuiSvgIcon-root": {
              color: "rgba(255,255,255,0.9) !important",
            },
            "& .MuiIconButton-colorSecondary": {
              color: "#FFFFFF !important",
            },
          }}
        >
          <HighlightOffIcon
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              cursor: "pointer",
            }}
            onClick={() => {
              history.push("/tradingrobots/main");
            }}
          />

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            {t("LinkAccount.linkYour")}
            <br />
            {platform === "MT5"
              ? t("LinkAccount.mt5Account", {
                  defaultValue: "cuenta MT5",
                })
              : t("LinkAccount.mt4Account")}
          </Typography>

          <Typography
            variant="body2"
            color="secondary"
            sx={{ mb: 2, px: 1, lineHeight: 1.3 }}
          >
            {t("LinkAccount.helperText", {
              defaultValue:
                "Introduce los datos exactamente como aparecen en tu plataforma MT4/MT5. Solo se usan para conectar tus robots.",
            })}
          </Typography>

          {/* Paso 1: Plataforma */}
          <Box sx={{ textAlign: "left", mt: 1 }}>
            <Typography
              variant="caption"
              color="secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              {t("LinkAccount.step1Platform", {
                defaultValue: "Paso 1 de 5 - Plataforma",
              })}
            </Typography>

            <TextField
              label={t("LinkAccount.platform", {
                defaultValue: "Plataforma",
              })}
              color="secondary"
              variant="outlined"
              name="platform"
              onChange={handlePlatformChange}
              value={platform}
              select
              fullWidth
              InputProps={{
                sx: { color: "secondary.main" },
                startAdornment: (
                  <InputAdornment position="start">
                    <DesktopWindowsIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ sx: { color: "secondary.main" } }}
            >
              {platformList.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <Typography variant="caption" color="secondary">
              {t("LinkAccount.platformHelper", {
                defaultValue: "Selecciona si tu cuenta es MT4/MT5.",
              })}
            </Typography>
          </Box>

          {/* Paso 2: Broker */}
          <Box sx={{ textAlign: "left", mt: 2 }}>
            <Typography
              variant="caption"
              color="secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              {t("LinkAccount.step2Broker", {
                defaultValue: "Paso 2 de 5 - Broker",
              })}
            </Typography>

            <TextField
              label={t("LinkAccount.broker")}
              color="secondary"
              variant="outlined"
              name="broker"
              onChange={handleBrokerChange}
              value={broker}
              inputRef={brokerRef}
              select
              fullWidth
              disabled={isBrokerDisabled}
              InputProps={{
                sx: { color: "secondary.main" },
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountBalanceIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ sx: { color: "secondary.main" } }}
            >
              {brokerList}
            </TextField>

            <Typography variant="caption" color="secondary">
              {isBrokerDisabled
                ? t("LinkAccount.platformHelper", {
                    defaultValue: "Selecciona si tu cuenta es MT4/MT5.",
                  })
                : t("LinkAccount.brokerHelper", {
                    defaultValue: "Selecciona el broker de tu cuenta de trading.",
                  })}
            </Typography>
          </Box>

          {/* Paso 3: Servidor */}
          <Box sx={{ textAlign: "left", mt: 2 }}>
            <Typography
              variant="caption"
              color="secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              {t("LinkAccount.step3Server", {
                defaultValue: "Paso 3 de 5 - Servidor",
              })}
            </Typography>

            <TextField
              label={t("LinkAccount.server")}
              color="secondary"
              variant="outlined"
              name="server"
              onChange={handleServerChange}
              value={server}
              select
              fullWidth
              disabled={isServerDisabled}
              inputRef={serverRef}
              InputProps={{
                sx: { color: "secondary.main" },
                startAdornment: (
                  <InputAdornment position="start">
                    <DnsIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ sx: { color: "secondary.main" } }}
            >
              {serverList}
            </TextField>

            <Typography variant="caption" color="secondary">
              {isServerDisabled
                ? t("LinkAccount.serverHelperDisabled", {
                    defaultValue: "Primero selecciona un broker.",
                  })
                : t("LinkAccount.serverHelper", {
                    defaultValue:
                      "Es el servidor que ves al iniciar sesión en MT4/MT5.",
                  })}
            </Typography>
          </Box>

          {/* Paso 4: Login */}
          <Box sx={{ textAlign: "left", mt: 2 }}>
            <Typography
              variant="caption"
              color="secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              {t("LinkAccount.step4Login", {
                defaultValue: "Paso 4 de 5 - Numero de cuenta",
              })}
            </Typography>

            <TextField
              label={
                platform === "MT5"
                  ? t("LinkAccount.loginMt5", {
                      defaultValue: "Numero cuenta MT5",
                    })
                  : t("LinkAccount.login")
              }
              color="secondary"
              type="text" // ✅ ya no es "number" → sin flechitas
              variant="outlined"
              name="login_no_autofill"
              onChange={handleLoginChange}
              value={login_no_autofill}
              fullWidth
              disabled={isLoginDisabled}
              inputRef={loginRef}
              autoComplete="off"
              InputProps={{
                sx: { color: "secondary.main" },
                startAdornment: (
                  <InputAdornment position="start">
                    <NumbersIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              InputLabelProps={{ sx: { color: "secondary.main" } }}
            />

            <Typography variant="caption" color="secondary">
              {isLoginDisabled
                ? t("LinkAccount.loginHelperDisabled", {
                    defaultValue: "Selecciona primero el servidor.",
                  })
                : t("LinkAccount.loginHelper", {
                    defaultValue:
                      "El número de cuenta que ves en la parte superior de tu plataforma.",
                  })}
            </Typography>
          </Box>

          {/* Paso 5: Password */}
          <Box sx={{ textAlign: "left", mt: 2 }}>
            <Typography
              variant="caption"
              color="secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              {t("LinkAccount.step5Password", {
                defaultValue: "Paso 5 de 5 - Contrasena",
              })}
            </Typography>

            <TextField
              label={t("LinkAccount.password")}
              type={showPassword ? "text" : "password"}
              variant="outlined"
              onChange={handlePasswordChange}
              value={password}
              fullWidth
              color="secondary"
              disabled={isPasswordDisabled}
              inputRef={passwordRef}
              InputProps={{
                sx: { color: "secondary.main" },
                autoComplete: "new-password",
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKeyIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      color="secondary"
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ sx: { color: "secondary.main" } }}
            />

            <Typography variant="caption" color="secondary">
              {isPasswordDisabled
                ? t("LinkAccount.passwordHelperDisabled", {
                    defaultValue: "Introduce primero el número de cuenta.",
                  })
                : t("LinkAccount.passwordHelper", {
                    defaultValue:
                      "Debe ser la contraseña de trading (no la contraseña de inversor).",
                  })}
            </Typography>
          </Box>

          {/* BOTÓN VINCULAR */}
          <Button
            sx={{
              mt: 3,
              mb: 1,
              bgcolor: "#FF8B00",
              color: "#FFFFFF",
              "&:hover": { bgcolor: "#CC6F00" },
            }}
            variant="contained"
            type="submit" // ✅ Enter envía el formulario
            disabled={loading || !isFormValid}
          >
            {t("LinkAccount.linkAccount")}
          </Button>

          {/* CTA crear cuenta */}
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1" color="secondary" fontWeight={500}>
              {t("LinkAccount.haveNotAccount")}
            </Typography>
            <Typography
              sx={{
                cursor: "pointer",
                textDecorationLine: "underline",
                color: "#FFB04C !important",
              }}
              component="a"
              onClick={() => {
                history.push("/tradingrobots/OpenAccount");
              }}
            >
              {t("LinkAccount.createAccountHere")}
            </Typography>
          </Box>

          <Box sx={{ mt: 1.5 }}>
            <Typography
              component="a"
              href="https://api.leadconnectorhq.com/widget/form/otjZ9X7hMZX6NBUOCiah"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#FFB04C !important",
                textDecoration: "underline",
                fontSize: "13px",
                lineHeight: 1.3,
                display: "inline-block",
              }}
            >
              ¿No encuentras tu broker en la lista? Solicítalo aquí
            </Typography>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default LinkAccount;

